import { Button } from "@/components/ui/Button";
import classes from "./Form.module.scss";
import { CardSelectorWithConfig } from "@/components/CardSelector";
import { useTranslation } from "@/hooks/useTranslation";
import { useDonationFormStore } from "@/stores";
import { useImperativeHandle, useRef, useState, type Ref } from "react";
import type { ImperativeHandle } from "@/types/Form";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";
import { DetailsStep } from "@/types/Navigation";

type FormStepCardSelectorProps = {
  onStepSubmit: () => void;
  ref?: Ref<ImperativeHandle>;
};

export function FormStepCardSelector(props: FormStepCardSelectorProps) {
  const { onStepSubmit, ref } = props;
  const t = useTranslation("_common");

  const cardSelectorRef = useRef<ImperativeHandle>(null);
  const [isValidating, setIsValidating] = useState(false);

  const { cardSelector } = useDonationFormStore();
  const { cardType, celebrationType, honouringForename, honouringSurname } =
    cardSelector;

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!cardSelectorRef.current) {
        return {
          isValid: true,
        };
      }

      return cardSelectorRef.current.validate();
    },
  }));

  const handleSubmit = async () => {
    setIsValidating(true);
    try {
      const validation = await cardSelectorRef.current?.validate();
      if (validation?.isValid) {
        onStepSubmit();
        dispatchEvent(EventName.STEP_SUBMIT, {
          step_name: DetailsStep.SelectCard,
          data: {
            card_type: cardType,
            celebration_type: celebrationType,
            honouring_forename: honouringForename,
            honouring_surname: honouringSurname,
          },
        });
      }
    } finally {
      setIsValidating(false);
    }
  };

  const showSubmitButton = cardType !== "";

  return (
    <div>
      <CardSelectorWithConfig ref={cardSelectorRef} />

      {showSubmitButton && (
        <div className={classes.pagination}>
          <Button
            variant="outlined"
            size="large"
            isBusy={isValidating}
            onClick={handleSubmit}
          >
            {t("continue")}
          </Button>
        </div>
      )}
    </div>
  );
}
